import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import { BuildingThreeIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'

import OpenLinkIcon from '../../../../../../../packages/ui/icons/open-link'
import useTranslate from '../../../../lib/hooks/use-translate'
import { staffQualificationEnumMapping } from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaffView } from '../../../../lib/types/models/affectedStaff/affected-staff'
import { OptionProperties } from '../../../../lib/types/models/options/option-properties'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import CountryFlag from '../../../elements/country-flag'

type Props = {
  affectedStaff?: AffectedStaffView
  onLinkClick?: () => void
}

const JourneyInformationCard = ({ affectedStaff, onLinkClick }: Props) => {
  const { t, currentLanguage } = useTranslate()
  const staff = affectedStaff?.staff
  const staffRequest = affectedStaff?.staffRequest

  const mainOptions =
    staffRequest?.centers
      .map((center) =>
        center.centerToJourneys.map(
          (ctj) => getTranslatedName(ctj.journey.mainOption, currentLanguage) || '—'
        )
      )
      .flat() || []

  const secondaryOptions =
    staffRequest?.centers
      .flatMap((center) =>
        center.centerToJourneys.map((ctj) =>
          ctj.journey.secondaryOptions.map(
            (so) => getTranslatedName(so.option, currentLanguage) || '—'
          )
        )
      )
      .flat() || []

  if (!staff || !staffRequest)
    return (
      <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
        {t('staff.personal.space.my.journeys.information.empty')}
      </Typography>
    )

  return (
    <InnerBox className="rounded-lg p-3 bg-white-hover">
      <div className="flex gap-3 mobile:flex-col">
        <img
          src={OptionProperties.getOptionPicture(
            affectedStaff?.staffRequest.centers[0]?.centerToJourneys[0]?.journey.mainOption
          )}
          className={cn('rounded-lg w-[100px] h-[100px] object-cover ')}
        />
        <div className="flex flex-col w-full gap-1">
          <div className="flex items-start  gap-2 mobile:items-start ">
            <CountryFlag
              possibleCountry={staffRequest?.country?.name}
              code={staffRequest?.country?.code}
            />
            <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
              {staffRequest.country?.name}
            </Typography>
            {staffRequest.startDate && staffRequest.endDate && (
              <Badge
                size="flexible"
                className="px-2 py-1"
                color={tailwindColors.secondaryRed.light.DEFAULT}
                textColor={tailwindColors.secondaryRed.DEFAULT}
                text={`${DateFormatter.DDMMYYYY(staffRequest.startDate)} - ${DateFormatter.DDMMYYYY(staffRequest.endDate)}`}
              />
            )}
          </div>
          <ul className="list-disc pl-5 flex flex-col gap-2 marker:text-grey-lighter marker:text-xs">
            <li>
              <Typography variant="secondary-body" color={tailwindColors.grey.lighter}>
                {t(staffQualificationEnumMapping[staffRequest.qualification].label)}
              </Typography>
            </li>
            <li>
              <Typography
                variant="secondary-body"
                color={tailwindColors.grey.lighter}
                className="text-wrap"
              >
                {[
                  staffRequest.formulas
                    .map((formula) => getTranslatedName(formula, currentLanguage) || '—')
                    .join(', '),
                  mainOptions.length > 0 ? mainOptions.join(', ') : null,
                  secondaryOptions.length > 0 ? secondaryOptions.join(', ') : null
                ]
                  .filter(Boolean)
                  .join(' - ')}
              </Typography>
            </li>
          </ul>
          <div className="flex items-center justify-between">
            <div>
              {affectedStaff.courseLocationDiffused && (
                <Badge
                  color={tailwindColors.primary.light.DEFAULT}
                  text={staffRequest.centers.map((center) => center.name).join(', ')}
                  iconPosition="start"
                  textColor={tailwindColors.primary.DEFAULT}
                  icon={<BuildingThreeIcon color={tailwindColors.primary.DEFAULT} />}
                />
              )}
            </div>
            <div
              className="bg-blue-light rounded-full p-1 cursor-pointer hover:bg-blue-light-active "
              onClick={onLinkClick}
            >
              <OpenLinkIcon color={tailwindColors.primary.DEFAULT} />
            </div>
          </div>
        </div>
      </div>
    </InnerBox>
  )
}

export default JourneyInformationCard
